import { useCallback, useEffect, useState } from 'react';
import {
  AUDIT_KIND_LABELS,
  AUDIT_KINDS,
  ROLE_LABELS,
  type AuditEntryView,
  type AuditKind,
  type AuditListView,
} from '@icetus/contracts';
import { Badge, Button, Card } from '@icetus/ui';
import { ApiError, fetchAudit } from '../api';

/**
 * 操作记录（P1-05）。
 *
 * 这一页的存在理由不是"我们想收集数据"，是客户会问一句**「谁把我页面下线了」**。
 * 在此之前这个问题的唯一答案是"不知道" —— 而客户通常不接受这个答案。
 *
 * 三个刻意的取舍：
 *
 *   - **按事件分组（tab）而不是让人搜全文**。日志不是搜索场景：客户打开它
 *     往往是先发现"页面没了"，再回头找。默认全部分组，按时间倒序，
 *     第一屏就是他最想看的。
 *   - **时间写绝对时间，不写"3 分钟前"**。审计要能对上"我昨天下午四点改的"，
 *     相对时间在翻到第二页时已经算不清了。
 *   - **没有"清空日志"**。能删的日志不叫日志；保留期由服务端策略决定，
 *     界面上不提供这个按钮，免得客户以为可以"眼不见为净"。
 */

/** 绝对时间。审计要和客户自己的记忆对得上，所以不用相对时间 */
function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * 谁是操作者。
 *
 * 三类身份必须分得开：客户自己、我们的运维（替他操作）、系统（支付回调、到期停站）。
 * 混成一句"管理员"会让客户以为自己的账号被人动过。
 */
function actorText(entry: AuditEntryView): { text: string; badge: string | null } {
  if (entry.actor.type === 'system') return { text: '系统', badge: '自动' };
  if (entry.actor.type === 'support') return { text: '平台运维', badge: '运维' };
  if (entry.actor.email) return { text: entry.actor.email, badge: null };
  // actor_id 是 on delete set null，但历史上可能有 id 无邮箱的行
  if (entry.actor.id) return { text: '已注销的用户', badge: null };
  return { text: '—', badge: null };
}

export function AuditPage({ tenantId }: { tenantId: string }) {
  const [view, setView] = useState<AuditListView | null>(null);
  const [kind, setKind] = useState<AuditKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(
    async (nextKind: AuditKind | null, cursor: string | null) => {
      const isMore = cursor !== null;
      if (isMore) setLoadingMore(true);
      else setLoading(true);
      try {
        const page = await fetchAudit(tenantId, { kind: nextKind, cursor });
        setView((prev) =>
          isMore && prev ? { ...page, items: [...prev.items, ...page.items] } : page,
        );
        setError(null);
      } catch (err) {
        // 没权限与"接口挂了"要分开说：前者客户该去找 owner 开权限，
        // 后者只能等我们修。都说"加载失败"会让他白等
        if (err instanceof ApiError && err.status === 403) {
          setError('只有管理员与所有者能看操作记录。需要的话请让所有者把你的角色改成管理员。');
        } else {
          setError(err instanceof ApiError ? err.message : '操作记录加载失败');
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [tenantId],
  );

  // 切分组等于换了一份数据：游标必须清空，否则会带着上一组的游标去翻页
  useEffect(() => {
    setView(null);
    void load(kind, null);
  }, [kind, load]);

  return (
    <Card
      title="操作记录"
      description="谁在什么时候改了什么。只记录会产生后果的操作 —— 发布、删除、改成员、动钱。"
    >
      <div className="audit-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={kind === null}
          className={kind === null ? 'pill pill--on' : 'pill'}
          onClick={() => setKind(null)}
        >
          全部
        </button>
        {AUDIT_KINDS.map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={kind === k}
            className={kind === k ? 'pill pill--on' : 'pill'}
            onClick={() => setKind(k)}
          >
            {AUDIT_KIND_LABELS[k]}
          </button>
        ))}
      </div>

      {error ? <p className="error">{error}</p> : null}
      {loading ? <p className="muted">加载中…</p> : null}

      {!loading && !error && view && view.items.length === 0 ? (
        <p className="muted">
          {kind ? '这个分类下还没有记录。' : '还没有记录。发布页面、改商品、加成员之后，这里会出现对应的条目。'}
        </p>
      ) : null}

      {view && view.items.length > 0 ? (
        <div className="tablewrap">
          <table className="table audit-table">
            <thead>
              <tr>
                <th>时间</th>
                <th>操作者</th>
                <th>做了什么</th>
                <th>来源</th>
              </tr>
            </thead>
            <tbody>
              {view.items.map((entry) => {
                const who = actorText(entry);
                return (
                  <tr key={entry.id}>
                    <td className="audit-time">{fmtTime(entry.createdAt)}</td>
                    <td>
                      <span>{who.text}</span>
                      {who.badge ? (
                        <Badge tone={entry.actor.type === 'support' ? 'warn' : 'neutral'}>
                          {who.badge}
                        </Badge>
                      ) : null}
                      {/* 角色是当时的快照：现在他被降成 viewer 了，
                          也不该让昨天那条"发布了页面"看起来没有权限 */}
                      {entry.actor.role ? (
                        <span className="muted audit-role">{ROLE_LABELS[entry.actor.role]}</span>
                      ) : null}
                    </td>
                    <td>
                      <span>{entry.label}</span>
                      {entry.targetLabel ? <code className="audit-target">{entry.targetLabel}</code> : null}
                      {entry.detail ? <div className="muted audit-detail">{entry.detail}</div> : null}
                    </td>
                    <td className="audit-ip">{entry.ip ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {view?.nextCursor ? (
        <div className="audit-more">
          <Button variant="ghost" disabled={loadingMore} onClick={() => void load(kind, view.nextCursor)}>
            {loadingMore ? '加载中…' : '再看更早的'}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}
